/**
 * Klient výlohy pro provizní systém (#36).
 *
 * Vlastní tenký `request` – schválně nesahá do sdíleného `api.ts`, aby
 * modul zůstal oddělený (souběžná práce více lidí). Chová se ale stejně:
 * CSRF hlavička u zápisu a `credentials: 'include'`, protože partnerský
 * přehled stojí na zákaznické session cookie.
 */

const CSRF_HEADER = 'x-requested-with';
const CSRF_VALUE = 'zooshopik-admin';

export class AffiliateError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'AffiliateError';
  }
}

export type CommissionStatus = 'pending' | 'approved' | 'cancelled' | 'paid';

/**
 * Provize tak, jak ji vidí partner. Datum, částky, stav – NIC o zákazníkovi.
 * Server tenhle tvar vynucuje; typ ho jen zrcadlí, ať se do UI nedostane
 * pole, které by tam nemělo být ani omylem.
 */
export interface PartnerCommission {
  readonly id: string;
  readonly placedAt: string;
  readonly source: 'click' | 'coupon';
  readonly status: CommissionStatus;
  readonly baseMinor: number;
  readonly commissionBp: number;
  readonly amountMinor: number;
  readonly clawback: boolean;
}

export interface PartnerOverview {
  readonly partner: {
    readonly code: string;
    readonly name: string;
    readonly status: 'active' | 'paused' | 'blocked';
    readonly commissionBp: number;
    readonly link: string;
  };
  readonly clicks: { readonly total: number; readonly recent: number };
  readonly totals: {
    readonly pendingMinor: number;
    readonly approvedMinor: number;
    readonly paidMinor: number;
    readonly cancelledMinor: number;
    readonly openClawbackMinor: number;
    readonly orderCount: number;
  };
  readonly commissions: readonly PartnerCommission[];
  readonly commissionTotal: number;
  readonly payouts: readonly {
    readonly id: string;
    readonly status: 'open' | 'paid';
    readonly grossMinor: number;
    readonly clawbackMinor: number;
    readonly totalMinor: number;
    readonly createdAt: string;
    readonly paidAt: string | null;
    readonly commissionCount: number;
  }[];
  readonly policy: { readonly attributionDays: number; readonly holdDays: number };
}

async function request<T>(path: string, options: { method?: string; body?: unknown } = {}): Promise<T> {
  const headers: Record<string, string> = { [CSRF_HEADER]: CSRF_VALUE };
  if (options.body !== undefined) headers['content-type'] = 'application/json';

  let response: Response;
  try {
    response = await fetch(path, {
      method: options.method ?? 'GET',
      headers,
      credentials: 'include',
      ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
    });
  } catch {
    throw new AffiliateError(0, 'network_error', 'Nepodařilo se spojit se serverem.');
  }

  const text = await response.text();
  let parsed: unknown = null;
  if (text !== '') {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = null;
    }
  }

  if (!response.ok) {
    const body = (parsed ?? {}) as { error?: string; message?: string };
    throw new AffiliateError(
      response.status,
      body.error ?? 'unknown_error',
      body.message ?? `Požadavek selhal (HTTP ${response.status}).`,
    );
  }
  return parsed as T;
}

export const affiliateApi = {
  /**
   * Ohlášení prokliku. Fail-silent: když se nepovede, návštěvník o tom
   * nesmí vědět – přišel si nakoupit, ne řešit naši statistiku.
   */
  click: (body: { code: string; visitorId?: string; sessionId?: string; path?: string }) =>
    request<{ ok: boolean }>('/api/affiliate/click', { method: 'POST', body }),

  me: () => request<PartnerOverview>('/api/affiliate/me'),
};
